import * as React from 'react';
import type { FieldPath, FieldValues, UseControllerProps, UseFormUnregister } from 'react-hook-form';

import { Input } from 'af-components/RHFFieldComponents/Input';

import type { InputCellType } from '../../RHFTableCellComponents/Input/types';
import type { RenderMethodParams } from '../RHFFieldWrapper';
import RHFFieldWrapper from '../RHFFieldWrapper';

type Props<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TTransformedValues = TFieldValues> =
	UseControllerProps<TFieldValues, TName, TTransformedValues> & {
		type: InputCellType;
		rhfId: number | string;
		label?: string;
		placeholder?: string;
		onChange?: (name: string) => void;
		onBlur: () => void;
		unregister: UseFormUnregister<TFieldValues>;
		fieldsLength?: number;
	};

function InputField<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const { name, rhfId, placeholder, type, onChange, onBlur, ...rest } = props;

	const renderInputField = React.useCallback((
		{
			field,
			error,
			onFieldChange,
			onFieldBlur,
			label,
		}: RenderMethodParams<TFieldValues, TName, string>	// the Input emits a raw string, not a change event
	) => (
		<Input
			elementKey={`${rhfId}`}
			error={error ?? undefined}
			key={name + rhfId}
			label={label}
			name={field.name}
			onBlur={onFieldBlur}
			onChange={onFieldChange}
			placeholder={placeholder}
			type={type}
			value={field.value}
		/>
	), [rhfId, name, placeholder, type]);

	return (
		<RHFFieldWrapper
			name={name}
			onBlur={onBlur}
			onChange={onChange}
			{...rest}
			render={renderInputField}
		/>
	);
}

export default React.memo(InputField) as typeof InputField;
